import React, { useState } from 'react';

import useIsLoggedIn from 'tradera-hooks/use-is-loggedin';
import { PICKS_FOR_YOU } from '../content-types';
import { trackContainerClick } from '../analytics';
import { LoadingSymbolCards } from 'static/script/components/recommendations/recommendation-loading-spinners';
import { useTranslator, useUrlLocalizer } from 'tradera-lang/translate';
import ALink from 'tradera-components/alink';
import ApplicationErrorBoundary from 'tradera-components/application-error-boundary';
import ContentSwitch from 'tradera-components/content-switch';
import Recommendations from 'tradera-components/recommendations';
import { recommendationsRequestBuilder } from 'tradera-components/recommendations/recommendations-requests';
import useIsMobile from 'tradera-hooks/use-is-mobile';

import 'tradera-components/recommendations/recommendations.scss';

const PicksForYou = () => {
    const isLoggedIn = useIsLoggedIn();
    const { t } = useTranslator();
    const [error, setError] = useState(false);
    const itemsPerPage = useIsMobile() ? 6 : 8;

    return error ? null : (
        <ApplicationErrorBoundary>
            <ContentSwitch
                className=""
                onSwitch={trackPicksForYouClick}
                options={{
                    endingSoon: {
                        node: (
                            <ApplicationErrorBoundary
                                fallback={<EndingSoonErrorMessage />}>
                                <Recommendations
                                    className="position-relative horizontal-scroll ml-md-1 ml-lg-n2 mr-md-2 mr-lg-2"
                                    collapseIfEmpty={false}
                                    createRequestUrl={recommendationsRequestBuilder(
                                        'Last_chance_infinite_scroll',
                                        itemsPerPage
                                    )}
                                    gtmLabel="Start Page Ending Soon"
                                    itemsPerPage={itemsPerPage}
                                    onError={() => setError(true)}
                                    loader={
                                        <LoadingSymbolCards
                                            itemsPerPage={itemsPerPage}
                                        />
                                    }>
                                    <LoadMoreButton recommendationMode="endingSoon" />
                                </Recommendations>
                            </ApplicationErrorBoundary>
                        ),
                        label: t('startPage_recommendation_endingSoon')
                    },
                    selected: {
                        node: (
                            <Recommendations
                                className="position-relative horizontal-scroll ml-md-1 ml-lg-n2 mr-md-2 mr-lg-2"
                                collapseIfEmpty={false}
                                createRequestUrl={recommendationsRequestBuilder(
                                    'Infinite_scroll_recommendation_slot',
                                    itemsPerPage
                                )}
                                gtmLabel="Start Page Selected"
                                itemsPerPage={itemsPerPage}
                                onError={() => setError(true)}
                                loader={
                                    <LoadingSymbolCards
                                        itemsPerPage={itemsPerPage}
                                    />
                                }>
                                <LoadMoreButton recommendationMode="selected" />
                            </Recommendations>
                        ),
                        label: t('startPage_recommendation_selected')
                    }
                }}
                title={
                    isLoggedIn
                        ? t('startPage_recommendation_justForYou')
                        : t('startPage_recommendation_trendingOnTradera')
                }
            />
        </ApplicationErrorBoundary>
    );
};

const trackPicksForYouClick = label => {
    trackContainerClick(PICKS_FOR_YOU, label);
};

const EndingSoonErrorMessage = () => {
    const inSPA = true;
    const { t } = useTranslator();
    const { toLocalizedUrl } = useUrlLocalizer();
    return (
        <div className="col-12 text-center pt-2 pb-4">
            <p className="size-paris mb-3">
                <strong>
                    {t('startPage_recommendation_endingSoonHeading')}
                </strong>
            </p>
            <p className="pb-2 mb-4">
                {t('startPage_recommendation_endingSoonText')}
            </p>
            <div className="col-12 text-center">
                <ALink
                    useLink={inSPA}
                    data-recommendations-reload
                    role="button"
                    className="btn btn-primary btn-wide"
                    href={toLocalizedUrl('/campaign/sista-chansen')}>
                    {`${t('startPage_recommendation_lastChance')} ->`}
                </ALink>
            </div>
        </div>
    );
};

const LoadMoreButton = ({ recommendationMode }) => {
    const inSPA = true;
    const { t } = useTranslator();
    const { toLocalizedUrl } = useUrlLocalizer();
    return (
        <div className="col-12 mt-2 text-center">
            <ALink
                useLink={inSPA}
                data-load-more-recommendations
                role="button"
                className="btn btn-primary btn-wide"
                onClick={() =>
                    trackPicksForYouClick('Picks for you button below')
                }
                href={toLocalizedUrl(
                    `/ma/endless-recommendation/${recommendationMode}`
                )}>
                {`${t('startPage_recommendation_showMore')} ->`}
            </ALink>
        </div>
    );
};

export default PicksForYou;
